import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DocDetalle } from './DocDetalle';
import { storeFile, deleteFile  } from '../../utils/indexedDB';
import { getMaxFileSize } from '../../services/configService';
//import logoSignature from "../../assets/logo_signature.png";
import domtoimage from 'dom-to-image';
import {getFullName} from "../../services/signService";
import SignatureAppearance from "./appearance/SignatureAppearance";
import {freezeComputedSizes, getDPR} from "../../utils/imageUtils";
import { getBrandLogoPng } from '../../branding/brandings';

const logoSrc = getBrandLogoPng();

const getUserName = () => {
    const toProperCase = (name) => {
        if (!name) return "Usuario";
        return name
            .split(/[\s]+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };
    const userInfo = sessionStorage.getItem('userinfo');
    if (userInfo) {
        const parsedUserInfo = JSON.parse(userInfo);
        return toProperCase(getFullName(parsedUserInfo));
    }
    return 'Usuario';
};

const getDefaultLogoPositionFromConfigs = () => {
    try {
        const raw = sessionStorage.getItem('configs');
        if (!raw) return 'BEFORE_TEXT';
        const cfg = JSON.parse(atob(raw));
        return (cfg?.DEFAULT_PAGE_SIGNATURE_LOGO_POSITION || 'BEFORE_TEXT').toString();
    } catch {
        return 'BEFORE_TEXT';
    }
};

export const CargaDocumentos = ({ setCanSign, setErrorMessageKey, setFileName  }) => {
    const { t } = useTranslation();
    const [file, setFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [maxSize, setMaxSize] = useState(null);
    const [signatureReady, setSignatureReady] = useState(false);
    const defaultSignatureRef = useRef();
    const logoLoaded = useRef(false);

    useEffect(() => {
        getMaxFileSize()
            .then(bytes => setMaxSize(bytes))
            .catch(() => {
                console.error('No se pudo obtener el tamaño máximo');
                setMaxSize(Infinity);
            });
    }, []);

    async function waitFontsAndImages(root) {
        try { if (document.fonts?.ready) await document.fonts.ready; } catch {}
        const imgs = Array.from(root?.querySelectorAll('img') || []);
        await Promise.all(imgs.map(i => i.decode?.().catch(() => null)));
    }

    const generateDefaultSignatureImage = async () => {
                const div = defaultSignatureRef.current;
                if (!div) return;

                    // 1) Determinar ancho de captura (usar el último ancho conocido o un fallback)
                        let targetW = 320; // fallback razonable para apariencia por defecto
                try {
                      const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
                      if (ap && ap.width && ap.width > 1) targetW = Math.round(ap.width);
                    } catch {}
        // Aplicar ancho explícito para que el layout no dependa de % ocultos
                const prevWidth  = div.style.width;
                const prevHeight = div.style.height;
                const prevLeft = div.style.left;
                const prevTop = div.style.top;
                const prevOpacity = div.style.opacity;
                const prevVisibility = div.style.visibility;
                const prevZIndex = div.style.zIndex;
                div.style.width  = `${targetW}px`;
                div.style.height = 'auto';
                div.style.left = '0';
                div.style.top = '0';
                div.style.opacity = '1';
                div.style.visibility = 'visible';
                div.style.zIndex = '-9999';

                    await waitFontsAndImages(div);
                const rect = div.getBoundingClientRect();
                const dpr  = getDPR();
                   // 2) Congelar tamaños reales (px) para que dom-to-image no reduzca el <img> (logo)
                        const restore = freezeComputedSizes(div);
               let dataUrl;
                try {
                        dataUrl = await domtoimage.toPng(div, {
                                bgcolor: null,
                                width:  Math.round(rect.width  * dpr),
                                height: Math.round(rect.height * dpr),
                                style: {
                                    transform: `scale(${dpr})`,
                                    transformOrigin: 'top left',
                                        width:  `${Math.round(rect.width)}px`,
                                        height: `${Math.round(rect.height)}px`,
                                    }
                        });
                    } finally {
                        restore();
                        // restaurar estilos del contenedor oculto
                            div.style.width  = prevWidth;
                        div.style.height = prevHeight;
                        div.style.left = prevLeft;
                        div.style.top = prevTop;
                        div.style.opacity = prevOpacity;
                        div.style.visibility = prevVisibility;
                        div.style.zIndex = prevZIndex;
                    }

                    // 3) Persistir dimensiones 1:1 (px CSS) para el visor
                      try {
                        const ap = JSON.parse(sessionStorage.getItem('appearance') || '{}');
                        ap.width  = Math.max(1, Math.round(rect.width));
                        ap.height = Math.max(1, Math.round(rect.height));
                        sessionStorage.setItem('appearance', JSON.stringify(ap));
                    } catch {}

                    // PNG listo
                       sessionStorage.setItem('signatureAppearance', dataUrl);
               setSignatureReady(true);
               setCanSign(true);
           };


    const handleFileChange = (e) => {
        const uploadedFile = e.target.files[0];
        e.target.value = '';
        validateAndSetFile(uploadedFile);
    };

    const handleDeleteFile = () => {
        if (file) {
            deleteFile(file.name).then(() => {
                setFile(null);
                setCanSign(false);
                setErrorMessageKey('');
                setFileName('');
            });
        }
        setFile(null);
    };

    const getFileSize = (size) => {
        const kbSize = (size / 1024).toFixed(2);
        return `${kbSize} kb`;
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const uploadedFile = e.dataTransfer.files[0];
        validateAndSetFile(uploadedFile);
    };

    const validateAndSetFile = (uploadedFile) => {
        if (!uploadedFile || uploadedFile.type !== 'application/pdf') {
            setErrorMessageKey('cargaDocumentos.invalidFileType');
            return;
        }
        if (maxSize != null && uploadedFile.size > maxSize) {
            // Convierte maxSize a MB con dos decimales
            const mb = (maxSize / 1024 / 1024).toFixed(0);
            const mensaje = t('cargaDocumentos.fileTooLarge', { size: mb });
            setErrorMessageKey(mensaje);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const fileData = reader.result.split(',')[1];
            storeFile(uploadedFile.name, fileData).then(async () => {
                setFile(uploadedFile);
                setErrorMessageKey('');
                setFileName(uploadedFile.name);
                sessionStorage.setItem('fileName', uploadedFile.name);
                setSignatureReady(false); // Bloquea el botón mientras se genera la imagen
                await generateDefaultSignatureImage();
            });
        };
        reader.readAsDataURL(uploadedFile);
    };

    const renderHiddenSignature = () => (
        <div
            ref={defaultSignatureRef}
            className="a-content"
            style={{
                position: 'fixed',
                left: '-10000px',
                top: 0,
                opacity: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
                zIndex: -9999,
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
            }}
        >
            <SignatureAppearance
                logoSrc={logoSrc}
                showLogo={true}
                signatureSrc={null}
                name={getUserName()}
                showName={true}
                showDate={false}
                showFreeText={false}
                alignment="center"
                logoPosition={getDefaultLogoPositionFromConfigs()}
            />
        </div>
    );




    return (
        <div className="component-carga-documentos">
            {renderHiddenSignature()}
            <div
                className={`carga-documento-content ${file ? 'carga-documento-content-disabled' : ''} ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <span className="material-symbols-outlined">cloud_upload</span>
                <p className="text">{t('cargaDocumentos.dragAndDrop')}</p>
                <p className="text-small space-between">{t('cargaDocumentos.or')}</p>

                <input
                    className="file-upload-input"
                    type="file"
                    onChange={handleFileChange}
                    disabled={!!file}
                />

                <button
                    className="local-upload btn-medium btn-primary-outline space-between"
                    type="button"
                    data-toggle="tooltip"
                    data-placement="top"
                    title={t('cargaDocumentos.selectFile')}
                    onClick={() => {
                        document.querySelector('.file-upload-input').click();
                    }}
                >
                    {t('cargaDocumentos.selectFileFromDevice')}
                </button>

                <p className="text-alert">{t('cargaDocumentos.fileAlert')}</p>

                {file && (
                    <DocDetalle
                        fileName={file.name}
                        fileSize={getFileSize(file.size)}
                        onDelete={handleDeleteFile}
                    />
                )}
            </div>
        </div>
    );
};
